<script lang="ts">
  import CalendarEventCard from "../../_shared/_modules/calendar-event-card"
  import type { BoardEvent, BoardMoveInteraction } from "../../../../../../_logic/calendar-types"

  type BoardPropertyRef = NonNullable<BoardEvent["property"]>
  type CalendarDragPreviewHandle = {
    show: (interaction: BoardMoveInteraction, clientX: number, clientY: number) => void
    move: (clientX: number, clientY: number) => void
    hide: () => void
  }

  interface Props {
    register?: (handle: CalendarDragPreviewHandle | null) => void
    formatAddress: (property?: BoardPropertyRef) => string
    employeeLabel?: string
    calendarSyncEnabled?: boolean
  }

  let {
    register = () => {},
    formatAddress,
    employeeLabel = "",
    calendarSyncEnabled = false,
  }: Props = $props()

  let previewEl: HTMLDivElement | null = null
  let activeInteraction = $state<BoardMoveInteraction | null>(null)
  let visible = $state(false)
  let width = $state(120)
  let height = $state(24)

  const formatTime = (value: Date) =>
    value.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })

  const previewItem = $derived.by(() => {
    if (!activeInteraction) return null

    const start = activeInteraction.currentStart
    const end = activeInteraction.currentEnd
    const event: ScheduleEvent = {
      ...activeInteraction.item.event,
      start_at: start.toISOString(),
      end_at: end.toISOString(),
      flex_type: activeInteraction.targetKind === "soft" ? "SOFT" : "HARD",
      timeframe_start_at:
        activeInteraction.targetKind === "soft"
          ? (activeInteraction.targetTimeframeStart ?? start).toISOString()
          : start.toISOString(),
      timeframe_end_at:
        activeInteraction.targetKind === "soft"
          ? (activeInteraction.targetTimeframeEnd ?? end).toISOString()
          : end.toISOString(),
    }

    return {
      ...activeInteraction.item,
      event,
      start,
      end,
      timeLabel: `${formatTime(start)} - ${formatTime(end)}`,
      top: 0,
      height,
      isPast: false,
    }
  })

  function transformFor(interaction: BoardMoveInteraction, clientX: number, clientY: number) {
    const left = clientX - interaction.sourceRect.pointerOffsetX
    const top = clientY - interaction.sourceRect.pointerOffsetY

    return `translate3d(${left}px, ${top}px, 0)`
  }

  function applyVisibility(nextVisible: boolean) {
    if (!previewEl) return

    previewEl.style.visibility = nextVisible ? "visible" : "hidden"
    previewEl.classList.toggle("calendar-drag-preview", nextVisible)
  }

  function show(interaction: BoardMoveInteraction, clientX: number, clientY: number) {
    const nextWidth = Math.max(120, interaction.sourceRect.width)
    const nextHeight = Math.max(24, interaction.sourceRect.height)

    if (previewEl) {
      previewEl.style.width = `${nextWidth}px`
      previewEl.style.height = `${nextHeight}px`
      previewEl.style.transform = transformFor(interaction, clientX, clientY)
    }

    activeInteraction = interaction
    width = nextWidth
    height = nextHeight
    visible = true
    applyVisibility(true)
  }

  function move(clientX: number, clientY: number) {
    if (!previewEl || !activeInteraction) return

    previewEl.style.transform = transformFor(activeInteraction, clientX, clientY)
  }

  function hide() {
    visible = false
    activeInteraction = null
    applyVisibility(false)

    if (previewEl) {
      previewEl.style.transform = "translate3d(-9999px, -9999px, 0)"
    }
  }

  const handle: CalendarDragPreviewHandle = { show, move, hide }

  $effect(() => {
    register(handle)

    return () => register(null)
  })
</script>

<div
  bind:this={previewEl}
  class={visible
    ? "calendar-drag-preview-host calendar-drag-preview"
    : "calendar-drag-preview-host"}
  aria-hidden="true"
>
  {#if previewItem}
    <CalendarEventCard
      item={previewItem}
      minEventHeight={height}
      {formatAddress}
      {employeeLabel}
      {calendarSyncEnabled}
      readOnly={true}
      showEventTooltips={false}
      overridePosition={{ top: 0, height }}
      useRouteInsets={false}
      isActive={true}
      preview={true}
      isPast={false}
      onOpen={() => undefined}
    />
  {/if}
</div>

<style lang="less">
  .calendar-drag-preview-host {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 5000;
    pointer-events: none;
    will-change: transform;
    visibility: hidden;
    width: 120px;
    height: 24px;
    transform: translate3d(-9999px, -9999px, 0);
    transition: none;
  }

  .calendar-drag-preview-host.calendar-drag-preview {
    visibility: visible;
  }

  .calendar-drag-preview :global(.event-card) {
    left: 0;
    right: 0;
    transform: none;
    opacity: 0.96;
    transition: none !important;
    animation: none !important;
  }
</style>
